'use client';

import { AtSign, ImageIcon, Loader2, Plus, SlidersHorizontal, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type CSSProperties, type ReactNode, type Ref, useCallback, useEffect, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { ElementsSelectorDialog } from '@/app/components/shared/ElementsSelectorDialog';
import { Img2VideoImgCropModal } from '@/app/components/shared/ImgCropModals/Img2VideoImgCropModal';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { Sheet, SheetContent } from '@/app/components/ui/sheet';
import { useProcessingCount } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import type { MediaChip } from './HomeGenerationFormMedia';
import {
  HomeGenerationDesktopUploadArea,
  HomeGenerationMobileMediaOptions,
  HomeGenerationMobileMediaStrip,
} from './HomeGenerationFormMediaAreas';
import { HomeGenerationFormModeSelects } from './HomeGenerationFormModeSelects';
import { HomeGenerationFormPromptInput } from './HomeGenerationFormPromptInput';
import { HomeGenerationFormSubmitButton } from './HomeGenerationFormSubmitButton';
import { HomeGenerationProgressPill } from './HomeGenerationProgressPill';
import {
  HomeGenerationMobileModelSheetContent,
  HomeGenerationMobileSettingsSheetContent,
  HomeGenerationSettingsBar,
} from './HomeGenerationSettingsBar';
import { useHomeGenerationFormController } from './hooks';

const getCollapsedMediaPreviewChips = (chips: MediaChip[]) =>
  chips.filter((chip) => chip.type === 'image' || chip.type === 'element').slice(-2);

type HorizontalScrollState = {
  isScrollable: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

const getHorizontalScrollState = (container: HTMLDivElement): HorizontalScrollState => {
  const isScrollable = container.scrollWidth > container.clientWidth + 1;

  return {
    isScrollable,
    canScrollLeft: isScrollable && container.scrollLeft > 0,
    canScrollRight: isScrollable && container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
  };
};

const getHorizontalScrollMaskImage = ({ isScrollable, canScrollLeft, canScrollRight }: HorizontalScrollState) => {
  if (!isScrollable) {
    return undefined;
  }

  if (canScrollLeft && canScrollRight) {
    return 'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)';
  }

  if (canScrollLeft) {
    return 'linear-gradient(to right, transparent 0, #000 32px, #000 100%)';
  }

  if (canScrollRight) {
    return 'linear-gradient(to right, #000 0, #000 calc(100% - 32px), transparent 100%)';
  }

  return undefined;
};

const getHorizontalScrollMaskStyle = (scrollState: HorizontalScrollState): CSSProperties | undefined => {
  const maskImage = getHorizontalScrollMaskImage(scrollState);

  return maskImage
    ? {
        maskImage,
        WebkitMaskImage: maskImage,
      }
    : undefined;
};

const horizontalScrollButtonClassName =
  'absolute top-1/2 z-20 flex h-8 w-5 -translate-y-1/2 items-center justify-center rounded-10 border border-white/[0.12] bg-system-black/80 text-system-white80 shadow-[0_8px_24px_rgba(0,0,0,0.18)] backdrop-blur-[20px] transition-[opacity,background-color,color] duration-200 hover:bg-system-black/90 hover:text-system-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-system-black/80';

const horizontalScrollButtonVisibilityClassName =
  'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100';

const HOME_GENERATION_SHEET_OVERLAY_STYLE = { zIndex: 44 };
const HOME_GENERATION_SHEET_CONTENT_STYLE = { zIndex: 45 };

const HomeGenerationScrollButton = ({
  direction,
  disabled,
  onClick,
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
    disabled={disabled}
    className={cn(
      horizontalScrollButtonClassName,
      horizontalScrollButtonVisibilityClassName,
      direction === 'left' ? 'left-0' : 'right-0',
    )}
    onClick={onClick}
  >
    {direction === 'left' ? <BaseLineLeft className="size-4" /> : <BaseLineRight className="size-4" />}
  </button>
);

const HomeGenerationCollapsedMediaThumb = ({
  chip,
  className,
  testId,
}: {
  chip: MediaChip;
  className?: string;
  testId: string;
}) => (
  <div
    data-testid={testId}
    className={cn(
      'absolute overflow-hidden border border-white/[0.12] bg-white/[0.06] shadow-[0_8px_20px_rgba(0,0,0,0.24)]',
      className,
    )}
  >
    {chip.preview ? (
      <>
        <img src={chip.preview} alt={chip.label} className="size-full object-cover" />
        {chip.loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
            <Loader2 className="size-3 animate-spin text-system-white" />
          </div>
        ) : null}
      </>
    ) : chip.loading ? (
      <div className="flex size-full items-center justify-center bg-white/[0.04]">
        <Loader2 className="size-3 animate-spin text-system-white64" />
      </div>
    ) : (
      <div className="flex size-full items-center justify-center bg-white/[0.04] text-system-white64">
        {chip.type === 'element' ? <AtSign className="size-3" /> : <ImageIcon className="size-3" />}
      </div>
    )}
  </div>
);

const HomeGenerationDesktopSettingsRail = ({ children }: { children: ReactNode }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<HorizontalScrollState>({
    isScrollable: false,
    canScrollLeft: false,
    canScrollRight: false,
  });

  const syncScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const nextScrollState = getHorizontalScrollState(container);
    setScrollState((currentScrollState) =>
      currentScrollState.isScrollable === nextScrollState.isScrollable &&
      currentScrollState.canScrollLeft === nextScrollState.canScrollLeft &&
      currentScrollState.canScrollRight === nextScrollState.canScrollRight
        ? currentScrollState
        : nextScrollState,
    );
  }, []);

  useEffect(() => {
    syncScrollState();
  }, [children, syncScrollState]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);

    return () => {
      container.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
    };
  }, [syncScrollState]);

  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
  }, []);

  return (
    <div className="group relative min-w-0 flex-1">
      <div
        ref={scrollContainerRef}
        className="hide-scrollbar flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto"
        style={getHorizontalScrollMaskStyle(scrollState)}
      >
        {children}
      </div>
      {scrollState.isScrollable && scrollState.canScrollLeft ? (
        <HomeGenerationScrollButton direction="left" disabled={false} onClick={handleScrollLeft} />
      ) : null}
      {scrollState.isScrollable && scrollState.canScrollRight ? (
        <HomeGenerationScrollButton direction="right" disabled={false} onClick={handleScrollRight} />
      ) : null}
    </div>
  );
};

const HomeGenerationCollapsedFloatingBar = ({
  controller,
  mobile = false,
  processingTaskCount,
}: {
  controller: ReturnType<typeof useHomeGenerationFormController>;
  mobile?: boolean;
  processingTaskCount: number;
}) => {
  const { floating, form, media, submit } = controller;
  const collapsedMediaPreviewChips = getCollapsedMediaPreviewChips(media.mediaChips);
  const hasCollapsedMediaPreview = collapsedMediaPreviewChips.length > 0;

  return (
    <div className={cn('relative', mobile ? '' : 'mx-auto max-w-[720px]')}>
      <HomeGenerationProgressPill
        submittedTasks={floating.submittedHomeTasks}
        processingTaskCount={processingTaskCount}
        fallbackTaskType={form.submitTaskType}
        className={cn('absolute right-0 z-10', mobile ? '-top-12' : '-top-[56px]')}
        disableEnterMotion={!mobile}
      />
      <div
        data-testid={mobile ? 'home-generation-mobile-collapsed-bar' : undefined}
        className={cn(
          'flex w-full items-center border border-white/[0.08] text-left text-system-white shadow-[0_18px_60px_rgba(0,0,0,0.24)]',
          mobile
            ? 'h-14 gap-3 rounded-[22px] bg-white/[0.04] px-3 backdrop-blur-[10px]'
            : 'h-[72px] gap-4 rounded-[20px] bg-[#35373B]/[0.48] px-5 backdrop-blur-[40px]',
        )}
      >
        <button
          type="button"
          className={cn(
            'relative flex shrink-0 items-center justify-center overflow-hidden bg-white/[0.04] text-system-white48 transition-colors hover:bg-white/[0.08] hover:text-system-white',
            mobile ? 'size-10 rounded-10' : 'h-14 w-12 rounded-[6px] px-0',
          )}
          onClick={floating.handleExpandFloatingForm}
        >
          {hasCollapsedMediaPreview ? (
            <div data-testid="home-generation-collapsed-media-preview" className="absolute inset-0">
              {collapsedMediaPreviewChips.length === 1 ? (
                <HomeGenerationCollapsedMediaThumb
                  chip={collapsedMediaPreviewChips[0]}
                  className="inset-[2px] rounded-[4px]"
                  testId="home-generation-collapsed-media-thumb-0"
                />
              ) : (
                <>
                  <HomeGenerationCollapsedMediaThumb
                    chip={collapsedMediaPreviewChips[0]}
                    className="inset-[3px] rotate-[4deg] rounded-[4px]"
                    testId="home-generation-collapsed-media-thumb-0"
                  />
                  <HomeGenerationCollapsedMediaThumb
                    chip={collapsedMediaPreviewChips[1]}
                    className="inset-[3px] rotate-[-4deg] rounded-[4px]"
                    testId="home-generation-collapsed-media-thumb-1"
                  />
                </>
              )}
            </div>
          ) : (
            <Plus className="size-5" />
          )}
        </button>
        <button type="button" className="min-w-0 flex-1 text-left" onClick={floating.handleExpandFloatingForm}>
          <span
            className={cn(
              'block truncate text-base',
              form.currentPlainPrompt.trim() ? 'text-system-white' : 'text-system-white40',
            )}
          >
            {floating.collapsedPromptLabel}
          </span>
        </button>
        <div className="shrink-0">
          <HomeGenerationFormSubmitButton
            isH5={form.isH5}
            isSubmitDisabled={form.isSubmitDisabled}
            isProcessing={form.isProcessing}
            maxImageCount={submit.maxImageCount}
            onBeforeSubmit={submit.handleBeforeSubmit}
            onCheckSettings={form.handleCheckSettings}
            onSubmitSuccess={submit.handleSubmitSuccess}
            shouldUpgradeForModel={form.shouldUpgradeForModel}
            submitTaskType={form.submitTaskType}
            validationErrorMessage={form.validationErrorMessage}
          />
        </div>
      </div>
    </div>
  );
};

export const HomeGenerationFormView = () => {
  const t = useTranslations();
  const controller = useHomeGenerationFormController();
  const { dialogs, floating, form, generation, imageCrop, media, multiFrame, refs, submit } = controller;
  const { processingTaskCount } = useProcessingCount();
  const { freeCreditsTasks, referenceModalOpen, referenceModalUpdateReason, userInfo } = useStore((state) => ({
    freeCreditsTasks: state.freeCreditsTasks,
    referenceModalOpen: state.referenceModalOpen,
    referenceModalUpdateReason: state.referenceModalUpdateReason,
    userInfo: state.userInfo,
  }));
  const [pendingCreatedSubjectSelection, setPendingCreatedSubjectSelection] = useState(false);
  const createdSubjectInitialUpdateReasonRef = useRef<typeof referenceModalUpdateReason>(undefined);
  const createdSubjectModalOpenedRef = useRef(false);
  const shouldReserveMobileTaskCenterSpace =
    form.isH5 &&
    Boolean(userInfo) &&
    freeCreditsTasks.some(
      (task) => task.name?.startsWith('newbie_task_') && !task.finished && task.state !== 'expired',
    );
  const getDropZoneSelectedImage = useCallback(
    (url: string) =>
      resizeSourceImage({
        url,
        maxImageSize: media.imageUploadMaxImageSize,
      }),
    [media.imageUploadMaxImageSize],
  );

  useEffect(() => {
    if (!pendingCreatedSubjectSelection) {
      return;
    }

    if (referenceModalOpen) {
      createdSubjectModalOpenedRef.current = true;
      return;
    }

    const hasNewUpdateReason = referenceModalUpdateReason !== createdSubjectInitialUpdateReasonRef.current;
    if (createdSubjectModalOpenedRef.current && hasNewUpdateReason && referenceModalUpdateReason?.type === 'update') {
      void generation.handleElementsSelect([referenceModalUpdateReason.reference]);
      createdSubjectInitialUpdateReasonRef.current = undefined;
      createdSubjectModalOpenedRef.current = false;
      setPendingCreatedSubjectSelection(false);
      return;
    }

    if (createdSubjectModalOpenedRef.current) {
      createdSubjectInitialUpdateReasonRef.current = undefined;
      createdSubjectModalOpenedRef.current = false;
      setPendingCreatedSubjectSelection(false);
    }
  }, [generation.handleElementsSelect, pendingCreatedSubjectSelection, referenceModalOpen, referenceModalUpdateReason]);

  const renderPromptInput = (
    textareaMinHeight: number,
    className?: string,
    inputRef?: Ref<HTMLTextAreaElement>,
    fitContainer?: boolean,
  ) => (
    <HomeGenerationFormPromptInput
      allowedModalities={form.currentAllowedModalities}
      className={className}
      currentPlainPrompt={form.currentPlainPrompt}
      handleBeforeAddMention={form.handleBeforeAddMention}
      handleBeforeDeleteMention={form.handleBeforeDeleteMention}
      inputRef={inputRef}
      mode={form.mode}
      onClear={form.handleClearAll}
      onPromptChange={form.handlePromptChange}
      onSelectSubject={() => media.handleSelectSubject()}
      promptPlaceholder={form.promptPlaceholder}
      referencePrompts={form.referencePrompts}
      referenceTextPrompt={form.referenceTextPrompt}
      showClearButton={false}
      textareaMinHeight={textareaMinHeight}
      fitContainer={fitContainer}
      updateReferenceTextPrompt={form.updateReferenceTextPrompt}
    />
  );

  const renderDesktopPanelContent = (inputRef?: Ref<HTMLTextAreaElement>) => (
    <>
      {multiFrame.isMultiFrameMode ? (
        <div className="flex min-h-[112px] min-w-0 flex-1 items-start">
          <HomeGenerationDesktopUploadArea controller={controller} />
        </div>
      ) : (
        <div className="group/prompt-area flex min-h-[112px] flex-1 items-start gap-5">
          <HomeGenerationDesktopUploadArea controller={controller} />
          {/* 素材堆叠卡展开时，提示词整体淡出到 40%，收起恢复 */}
          <div className="flex min-h-[120px] min-w-0 flex-1 flex-col opacity-100 transition-opacity group-has-[[data-reference-stacks-expanded=true]]/prompt-area:opacity-40">
            {renderPromptInput(112, 'min-h-[112px] text-base leading-6 lg:text-sm', inputRef, true)}
          </div>
        </div>
      )}

      {/* items-end：设置项高 36、发送按钮高 48，底部对齐让两者下沿齐平。 */}
      <div className="flex items-end justify-between gap-3">
        <HomeGenerationDesktopSettingsRail>
          <HomeGenerationFormModeSelects
            generationMethodLabelMap={form.generationMethodLabelMap}
            generationModeOptions={form.generationModeOptions}
            generationTypeOptions={form.generationTypeOptions}
            mode={form.mode}
            onGenerationMethodChange={generation.handleGenerationMethodChange}
            onGenerationModeChange={generation.handleGenerationModeChange}
            onGenerationTypeChange={generation.handleGenerationTypeChange}
          />
          <HomeGenerationSettingsBar
            className="min-w-0 shrink-0"
            mode={form.mode}
            settings={form.settings}
            onUpdateSettings={form.updateSettings}
          />
        </HomeGenerationDesktopSettingsRail>
        <HomeGenerationFormSubmitButton
          isH5={form.isH5}
          isSubmitDisabled={form.isSubmitDisabled}
          isProcessing={form.isProcessing}
          maxImageCount={submit.maxImageCount}
          onBeforeSubmit={submit.handleBeforeSubmit}
          onCheckSettings={form.handleCheckSettings}
          onSubmitSuccess={submit.handleSubmitSuccess}
          shouldUpgradeForModel={form.shouldUpgradeForModel}
          submitTaskType={form.submitTaskType}
          validationErrorMessage={form.validationErrorMessage}
        />
      </div>
    </>
  );

  const renderDesktopPanel = (inputRef?: Ref<HTMLTextAreaElement>, exiting = false) => (
    <div
      className={cn(
        'relative origin-bottom motion-reduce:animate-none',
        exiting ? 'pointer-events-none animate-floating-expand-out' : 'animate-floating-expand-in',
      )}
    >
      <HomeGenerationProgressPill
        submittedTasks={floating.submittedHomeTasks}
        processingTaskCount={processingTaskCount}
        fallbackTaskType={form.submitTaskType}
        className="-top-[56px] absolute right-0 z-10 bg-[#35373B]/[0.48]"
        disableEnterMotion={true}
        dimContentUntilHover={true}
      />
      <div className="relative rounded-[28px] border border-white/[0.08] bg-[#35373B]/[0.48] shadow-[0_18px_60px_rgba(0,0,0,0.24)] transition-colors hover:border-white/[0.24]">
        <div
          data-testid="home-generation-desktop-panel-backdrop"
          aria-hidden={true}
          className="pointer-events-none absolute inset-0 rounded-[28px] backdrop-blur-[70px]"
        />
        <div className="relative flex min-h-[206px] flex-col p-4">{renderDesktopPanelContent(inputRef)}</div>
      </div>
    </div>
  );

  const renderDesktopInlinePanel = () => (
    <div className="group relative">
      <div className="relative z-10 overflow-hidden rounded-[28px] border border-white/[0.08] bg-white/[0.06] text-system-white shadow-[0_18px_60px_rgba(0,0,0,0.24)] transition-colors hover:border-white/[0.24]">
        <div
          data-testid="home-generation-desktop-panel-backdrop"
          aria-hidden={true}
          className="pointer-events-none absolute inset-0 backdrop-blur-[10px]"
        />
        <div className="relative flex min-h-[206px] flex-col p-4">{renderDesktopPanelContent()}</div>
      </div>
      <div data-testid="home-generation-desktop-progress-slot" className="-mt-8 relative h-20 shrink-0 pb-4">
        <HomeGenerationProgressPill
          submittedTasks={floating.submittedHomeTasks}
          processingTaskCount={processingTaskCount}
          fallbackTaskType={form.submitTaskType}
          className="home-generation-desktop-progress"
          variant="desktopInline"
        />
      </div>
    </div>
  );

  return (
    <MediaDropZone
      className="contents"
      acceptImage={media.dropZone.acceptImage}
      acceptVideo={media.dropZone.acceptVideo}
      maxCount={media.dropZone.maxCount}
      maxImageCount={media.dropZone.maxImageCount}
      maxVideoCount={media.dropZone.maxVideoCount}
      minVideoDuration={media.dropZone.minVideoDuration}
      maxVideoDuration={media.dropZone.maxVideoDuration}
      maxVideoSize={media.dropZone.maxVideoSize}
      disabled={media.dropZone.disabled}
      getSelectedImage={getDropZoneSelectedImage}
      captureCoverImage={true}
      uploadCoverImage={true}
      allowTempMode={true}
      emitUploadingPreview={true}
      enablePaste={true}
      onChange={media.handleUploadReferenceMedia}
    >
      <section
        ref={refs.desktopFormAnchorRef}
        className={cn(
          'relative',
          form.isH5 &&
            cn(
              'fixed right-4 left-4',
              !floating.mobileFormExpanded && shouldReserveMobileTaskCenterSpace
                ? 'bottom-[calc(var(--ios-safe-bottom)+44px)]'
                : 'bottom-[calc(var(--ios-safe-bottom)-24px)]',
              floating.mobileFormExpanded ? 'z-40' : 'z-30',
            ),
        )}
        style={
          !form.isH5 && floating.desktopFormFloating && floating.desktopFormPinnedHeight !== null
            ? {
                height: `${floating.desktopFormPinnedHeight}px`,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <Img2VideoImgCropModal
          onClose={imageCrop.handleCloseCropModal}
          onCancel={imageCrop.handleCancelCrop}
          onConfirm={imageCrop.handleBatchSaveCropImages}
          cropImagePrompts={imageCrop.cropImagePrompts}
          activeIndex={imageCrop.cropFrameIndex}
          maxImageCount={submit.maxImageCount}
          handleClickImage={imageCrop.setCropFrameIndex}
          handleExchange={imageCrop.handleSwapImages}
          onChange={imageCrop.handleSingleImageChange}
          maxImageSize={media.imageUploadMaxImageSize}
          allowTempMode={true}
        />
        {form.isH5 ? (
          !floating.mobileFormExpanded ? (
            <HomeGenerationCollapsedFloatingBar
              controller={controller}
              mobile={true}
              processingTaskCount={processingTaskCount}
            />
          ) : (
            <>
              <button
                type="button"
                aria-label={t('elements.elementDialog.close')}
                className="fixed inset-0 z-20 h-screen w-screen bg-black/[0.56] backdrop-blur-[7px]"
                onClick={() => floating.setMobileFormExpanded(false)}
              />
              <div className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+112px)] left-4 z-30">
                <div className="relative">
                  <HomeGenerationProgressPill
                    submittedTasks={floating.submittedHomeTasks}
                    processingTaskCount={processingTaskCount}
                    fallbackTaskType={form.submitTaskType}
                    className="-top-12 absolute right-0 z-10"
                    compact={true}
                  />
                  <div className="relative min-h-[188px] rounded-[20px] border border-white/[0.08] bg-white/[0.04] px-3 pt-3 pb-[52px] text-system-white shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-[28px]">
                    {multiFrame.isMultiFrameMode || media.showMobileMediaRow ? (
                      <HomeGenerationMobileMediaStrip controller={controller} />
                    ) : null}
                    {!multiFrame.isMultiFrameMode ? (
                      <div className={cn('min-w-0', media.showMobileMediaRow ? 'mt-3' : '')}>
                        {renderPromptInput(
                          56,
                          'min-h-[56px] text-base leading-6 placeholder:text-system-white28 lg:text-base [&_.ProseMirror]:text-system-white [&_.ProseMirror]:text-base [&_.ProseMirror]:leading-6 [&>div.pointer-events-none]:text-system-white28',
                          refs.plainPromptRef,
                        )}
                      </div>
                    ) : null}

                    <div className="absolute right-3 bottom-3 left-3 flex h-8 items-center gap-1.5">
                      <HomeGenerationFormModeSelects
                        generationMethodLabelMap={form.generationMethodLabelMap}
                        generationModeOptions={form.generationModeOptions}
                        generationTypeOptions={form.generationTypeOptions}
                        mobile={true}
                        mode={form.mode}
                        onGenerationMethodChange={generation.handleGenerationMethodChange}
                        onGenerationModeChange={generation.handleGenerationModeChange}
                        onGenerationTypeChange={generation.handleGenerationTypeChange}
                      />
                      <button
                        type="button"
                        className="flex h-8 w-[104px] shrink-0 items-center justify-center rounded-10 bg-white/[0.08] px-2 text-system-white text-xs"
                        onClick={() => {
                          dialogs.setMobileModelOpen(true);
                        }}
                      >
                        <span className="truncate">{form.modelVersionLabel}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={t('home.generationForm.settings.title')}
                        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-system-white64"
                        onClick={() => {
                          dialogs.setMobileSettingsOpen(true);
                        }}
                      >
                        <SlidersHorizontal className="size-4" />
                      </button>
                      <div className="shrink-0">
                        <HomeGenerationFormSubmitButton
                          isH5={form.isH5}
                          isSubmitDisabled={form.isSubmitDisabled}
                          isProcessing={form.isProcessing}
                          maxImageCount={submit.maxImageCount}
                          onBeforeSubmit={submit.handleBeforeSubmit}
                          onCheckSettings={form.handleCheckSettings}
                          onSubmitSuccess={submit.handleSubmitSuccess}
                          shouldUpgradeForModel={form.shouldUpgradeForModel}
                          submitTaskType={form.submitTaskType}
                          validationErrorMessage={form.validationErrorMessage}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        ) : (
          <>
            {renderDesktopInlinePanel()}
            {floating.desktopFloatingRendered ? (
              <div
                ref={floating.desktopFloatingLayerRef}
                data-testid="home-generation-desktop-floating-layer"
                className={cn(
                  'fixed right-6 bottom-[calc(env(safe-area-inset-bottom)+40px)] left-6 z-30 mx-auto max-w-[996px] motion-reduce:animate-none',
                  floating.desktopFloatingExiting
                    ? 'pointer-events-none animate-floating-bar-out'
                    : 'animate-floating-bar-in',
                )}
              >
                {floating.desktopExpandRendered ? (
                  renderDesktopPanel(refs.plainPromptRef, floating.desktopExpandExiting)
                ) : (
                  <HomeGenerationCollapsedFloatingBar
                    controller={controller}
                    processingTaskCount={processingTaskCount}
                  />
                )}
              </div>
            ) : null}
          </>
        )}

        <Sheet open={dialogs.mobileSettingsOpen} onOpenChange={dialogs.setMobileSettingsOpen}>
          <SheetContent
            side="bottom"
            showClose={false}
            overlayStyle={HOME_GENERATION_SHEET_OVERLAY_STYLE}
            style={HOME_GENERATION_SHEET_CONTENT_STYLE}
            className="flex h-[48dvh] max-h-[388px] min-h-[360px] flex-col rounded-t-[24px] border-none bg-[#17191D] p-5"
          >
            <HomeGenerationMobileSettingsSheetContent
              mode={form.mode}
              settings={form.settings}
              onUpdateSettings={form.updateSettings}
              onClose={() => dialogs.setMobileSettingsOpen(false)}
            />
          </SheetContent>
        </Sheet>

        <Sheet open={dialogs.mobileModelOpen} onOpenChange={dialogs.setMobileModelOpen}>
          <SheetContent
            side="bottom"
            showClose={false}
            overlayStyle={HOME_GENERATION_SHEET_OVERLAY_STYLE}
            style={HOME_GENERATION_SHEET_CONTENT_STYLE}
            className="flex h-[38dvh] max-h-[308px] min-h-[306px] flex-col rounded-t-[24px] border-none bg-[#17191D] px-3 pt-4 pb-3"
          >
            <HomeGenerationMobileModelSheetContent
              mode={form.mode}
              settings={form.settings}
              onClose={() => dialogs.setMobileModelOpen(false)}
            />
          </SheetContent>
        </Sheet>

        <Sheet open={dialogs.mobileMediaOptionsOpen} onOpenChange={dialogs.setMobileMediaOptionsOpen}>
          <SheetContent
            side="bottom"
            showClose={false}
            overlayStyle={HOME_GENERATION_SHEET_OVERLAY_STYLE}
            style={HOME_GENERATION_SHEET_CONTENT_STYLE}
            className="flex max-h-[45dvh] flex-col rounded-t-[24px] border-none bg-[#17191D] p-4"
          >
            <div className="mb-4 flex h-8 shrink-0 items-center justify-start">
              <button
                type="button"
                className="flex size-8 items-center justify-center rounded-full text-system-white64"
                onClick={() => dialogs.setMobileMediaOptionsOpen(false)}
              >
                <X className="size-5" />
              </button>
            </div>
            <HomeGenerationMobileMediaOptions controller={controller} />
          </SheetContent>
        </Sheet>

        <ElementsSelectorDialog
          mode={form.isH5 ? 'sheet' : 'dialog'}
          isOpen={dialogs.elementsSelectorOpen}
          onClose={() => dialogs.setElementsSelectorOpen(false)}
          onCreateOpen={() => {
            createdSubjectInitialUpdateReasonRef.current = referenceModalUpdateReason;
            createdSubjectModalOpenedRef.current = false;
            setPendingCreatedSubjectSelection(true);
            dialogs.setElementsSelectorOpen(false);
          }}
          onSelect={(elements, autoClose = true) => {
            // 视频主体要等时长探测完才知道能不能加，被拒时 handleElementsSelect 内部会 toast
            // 并保持弹窗不关，所以这里只在明确要求自动关闭时才关。
            void generation.handleElementsSelect(elements);
            if (autoClose) {
              dialogs.setElementsSelectorOpen(false);
            }
          }}
          remainingSelections={dialogs.elementsSelectorRemainingSelections}
          maxReferenceCount={dialogs.elementsSelectorMaxReferenceCount}
          disabledIds={form.referencePrompts.filter((prompt) => prompt.type === 'element').map((prompt) => prompt.id)}
          allowedModalities={dialogs.elementsSelectorAllowedModalities ?? form.currentAllowedModalities}
        />
      </section>
    </MediaDropZone>
  );
};
